import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import ChangelogModal from 'src/components/modals/ChangelogModal/ChangelogModal';
import { ChangelogEntry, fetchChangelogEntry, fetchUnseenChangelog, markChangelogSeen } from 'src/services/changelog';
import { useProfile } from 'src/utils/ProfileService';

const RECHECK_MS = 10 * 60 * 1000;

/**
 * Two jobs on every authenticated page:
 * 1. Shows unseen release notes once per user after login. An entry counts as
 *    seen only when the user ticks "don't show again" before closing; any
 *    other close hides it for the rest of the session, so it returns on the
 *    next login. A reload while it is open brings it back.
 * 2. Opens a single entry when the URL carries `?changelog=<id>` (notification
 *    click, chat link) and drops the parameter again on close.
 */
const ChangelogGate: React.FC = () => {
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [unseen, setUnseen] = useState<ChangelogEntry[]>([]);
  const [requested, setRequested] = useState<ChangelogEntry | null>(null);
  const loadedForUser = useRef<number | null>(null);
  const lastCheck = useRef(0);
  const dismissed = useRef(new Set<number>());
  const requestedId = Number(searchParams.get('changelog')) || null;

  // Checked once per login and again whenever the tab becomes visible after
  // at least ten minutes, so a release published while the portal stays open
  // in a background tab still pops up without a reload.
  useEffect(() => {
    if (!profile?.id) return undefined;
    let cancelled = false;
    const load = () => {
      lastCheck.current = Date.now();
      fetchUnseenChangelog()
        .then(entries => {
          const pending = entries.filter(entry => !dismissed.current.has(entry.id));
          if (!cancelled && pending.length) setUnseen(prev => (prev.length ? prev : pending));
        })
        .catch(() => undefined);
    };
    if (loadedForUser.current !== profile.id) {
      loadedForUser.current = profile.id;
      load();
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastCheck.current > RECHECK_MS) load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [profile?.id]);

  const clearRequested = useCallback(() => {
    setRequested(null);
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('changelog');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (!requestedId || !profile?.id) {
      setRequested(null);
      return undefined;
    }
    let cancelled = false;
    fetchChangelogEntry(requestedId)
      .then(entry => {
        if (!cancelled) setRequested(entry);
      })
      .catch(() => {
        if (!cancelled) clearRequested();
      });
    return () => {
      cancelled = true;
    };
  }, [requestedId, profile?.id, clearRequested]);

  const refreshList = () => queryClient.invalidateQueries({ queryKey: ['changelog'] });

  const closeRequested = () => {
    if (requested && !requested.seen)
      markChangelogSeen(requested.id)
        .then(refreshList)
        .catch(() => undefined);
    setUnseen(prev => prev.filter(entry => entry.id !== requested?.id));
    clearRequested();
  };

  const closeUnseen = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      Promise.all(unseen.map(entry => markChangelogSeen(entry.id).catch(() => undefined))).then(refreshList);
    } else {
      unseen.forEach(entry => dismissed.current.add(entry.id));
    }
    setUnseen([]);
  };

  if (requested) return <ChangelogModal entries={[requested]} isOpen onClose={closeRequested} />;
  if (unseen.length) return <ChangelogModal entries={unseen} isOpen onClose={closeUnseen} showDontShowAgain />;
  return null;
};

export default ChangelogGate;
